import React from "react";
import styles from "./DataTable.module.scss";
import delete_icon from "../../../assets/images/actions/delete.svg";
import edit_icon from "../../../assets/images/actions/edit.svg";
import ArrowLeft from "../../../assets/images/svgs/ArrowLeft";
import ArrowRight from "../../../assets/images/svgs/ArrowRight";
import { useTranslation } from "react-i18next";

type RenderFn = (value: any, row?: any, index?: number) => React.ReactNode;

export interface Column {
  key: string;
  header?: string;
  render?: RenderFn;
  className?: string;
}

export interface PaginationData {
  total: number | null;
  page?: number;
  limit?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

interface DataTableProps {
  data: any[];
  columns?: Column[];
  className?: string;
  noDataMessage?: string;
  actions?: boolean;
  onEdit?: (id: number) => void;
  onDelete?: (id: number, hasMultipleVariants?: boolean) => void;
  paginationData?: PaginationData;
  isProductVariantTable?: boolean;
  expandedProducts?: Set<number>;
  onToggleExpand?: (productId: number) => void;
}

export default function DataTable({
  data,
  columns,
  className = "",
  noDataMessage = "",
  actions,
  onEdit,
  onDelete,
  paginationData,
  isProductVariantTable,
  expandedProducts,
  onToggleExpand,
}: DataTableProps) {
  const { t } = useTranslation("translation");
  const inferredColumns: Column[] = React.useMemo(() => {
    if (columns && columns.length > 0) return columns;
    if (!data || data.length === 0) return [];

    const first = data[0];
    const keys = Object.keys(first);
    return keys.map((k) => ({ key: k, header: k }));
  }, [columns, data]);

  const cols = inferredColumns;

  if (!data || data.length === 0) {
    return (
      <div className={`${styles.wrapper} ${className}`}>
        <div className={styles.noData}>{noDataMessage || t("no_data")}</div>
      </div>
    );
  }

  const totalPages = paginationData?.total
    ? Math.ceil(paginationData.total / (paginationData.limit || 10))
    : 1;

  const start =
    ((paginationData?.page || 1) - 1) * (paginationData?.limit || 10) + 1;
  const end = Math.min(
    start + (paginationData?.limit || 10) - 1,
    paginationData?.total || 0,
  );

  return (
    <div className={styles.data_table}>
      <div className={`${styles.wrapper} ${className}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} className={`${styles.th} ${c.className ?? ""}`}>
                  {c.header ?? c.key}
                </th>
              ))}
              {actions && <th className={styles.th}>{t("actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => {
              const hasMultiple = row._hasMultiple;
              const isExpanded = expandedProducts?.has(row.product_id);
              const variants = row._variants || [row];

              return (
                <React.Fragment key={rowIndex}>
                  <tr className={styles.tr}>
                    {cols.map((c, colIndex) => {
                      const value = row == null ? undefined : row[c.key];
                      return (
                        <td
                          key={c.key}
                          className={`${styles.td} ${c.className ?? ""}`}
                        >
                          {colIndex === 0 && hasMultiple && onToggleExpand ? (
                            <div className={styles.expandCell}>
                              <button
                                type="button"
                                className={styles.expandButton}
                                onClick={() => onToggleExpand(row.product_id)}
                              >
                                {isExpanded ? "▼" : "▶"}
                              </button>
                              <span>
                                {c.render
                                  ? c.render(value, row, rowIndex)
                                  : (value ?? "")}
                              </span>
                              {hasMultiple && (
                                <span className={styles.variantCount}>
                                  ({variants.length})
                                </span>
                              )}
                            </div>
                          ) : (
                            <>
                              {c.render
                                ? c.render(value, row, rowIndex)
                                : (value ?? "")}
                            </>
                          )}
                        </td>
                      );
                    })}
                    {actions && (
                      <td className={styles.td}>
                        <div className={styles.icons}>
                          {onDelete && (
                            <div
                              className={styles.icon}
                              onClick={() => onDelete(row.id, hasMultiple)}
                            >
                              <img src={delete_icon} alt="delete" />
                            </div>
                          )}
                          {onEdit && (
                            <div
                              className={styles.icon}
                              onClick={() =>
                                onEdit(
                                  isProductVariantTable
                                    ? row.product_id
                                    : row.id,
                                )
                              }
                            >
                              <img src={edit_icon} alt="edit" />
                            </div>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>

                  {isExpanded &&
                    hasMultiple &&
                    variants.map((variant: any, vIndex: number) => (
                      <tr
                        key={`${rowIndex}-variant-${vIndex}`}
                        className={`${styles.tr} ${styles.subVariant}`}
                      >
                        {cols.map((c, colIndex) => {
                          const value =
                            variant == null ? undefined : variant[c.key];
                          return (
                            <td
                              key={c.key}
                              className={`${styles.td} ${c.className ?? ""}`}
                            >
                              {colIndex === 0 ? (
                                <div className={styles.variantCell}>
                                  <span className={styles.variantBadge}>
                                    V{vIndex + 1}
                                  </span>
                                  <span>
                                    {c.render
                                      ? c.render(value, variant, rowIndex)
                                      : (value ?? "")}
                                  </span>
                                </div>
                              ) : (
                                <>
                                  {c.render
                                    ? c.render(value, variant, rowIndex)
                                    : (value ?? "")}
                                </>
                              )}
                            </td>
                          );
                        })}
                        {actions && (
                          <td className={styles.td}>
                            <div className={styles.icons}>
                              {onDelete && (
                                <div
                                  className={styles.icon}
                                  onClick={() => onDelete(variant.id, false)}
                                >
                                  <img src={delete_icon} alt="delete" />
                                </div>
                              )}
                              {onEdit && (
                                <div
                                  className={styles.icon}
                                  onClick={() =>
                                    onEdit(
                                      isProductVariantTable
                                        ? variant.product_id
                                        : variant.id,
                                    )
                                  }
                                >
                                  <img src={edit_icon} alt="edit" />
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {paginationData &&
        paginationData.total != null &&
        paginationData.total > 10 && (
          <div className={styles.pagination_section}>
            <div className={styles.pagination_pages}>
              <p className={styles.current_pages}>
                {start}-{end}
              </p>
              <p className={styles.all_pages}>of {paginationData.total}</p>
            </div>
            <div className={styles.pagination_controls}>
              <p className={styles.page_info}>
                Page {paginationData.page} of {totalPages}
              </p>
              <div className={styles.page_counts}>
                <p className={styles.count_title}>Rows per page: </p>
                <select
                  className={styles.select}
                  value={paginationData.limit}
                  onChange={(e) =>
                    paginationData.onLimitChange?.(Number(e.target.value))
                  }
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <div className={styles.arrows}>
                <div
                  className={styles.arrow}
                  onClick={() =>
                    paginationData.onPageChange?.(
                      (paginationData.page || 1) - 1,
                    )
                  }
                >
                  <ArrowLeft />
                </div>
                <div
                  className={styles.arrow}
                  onClick={() =>
                    paginationData.onPageChange?.(
                      (paginationData.page || 1) + 1,
                    )
                  }
                >
                  <ArrowRight />
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
